import { DeleteOutlined, HistoryOutlined } from '@ant-design/icons';
import { Button, Empty, Popconfirm, Tag, Tooltip, Typography } from 'antd';

import type { JobSummary } from '../../../src/types.ts';
import { fmtDateTime, fmtPrice, MONTHS } from '../lib/format.ts';

interface Props {
  items: JobSummary[] | null;
  currentId: string | null;
  onOpen: (job: JobSummary) => void;
  onDelete: (job: JobSummary) => void;
}

export function summaryTitle(j: JobSummary): string {
  const p = j.params;
  const o = p.origins?.length ? p.origins.join('/') : p.origin;
  const d = p.destinations?.length ? p.destinations.join('/') : p.destination;
  return `${o} ${p.type === 'ROUNDTRIP' ? '⇆' : '→'} ${d} · ${MONTHS[p.month - 1] ?? p.month} ${p.year}`;
}

/** Past month searches — results are kept on the server, so any of them reopens instantly. */
export function RecentSearches({ items, currentId, onOpen, onDelete }: Props) {
  if (items && items.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Your searches will be listed here." />;
  }
  return (
    <div className="recent">
      {(items ?? []).map((j) => {
        const p = j.params;
        const pct = j.progress.daysTotal ? Math.round((j.progress.daysDone / j.progress.daysTotal) * 100) : 0;
        return (
          <div key={j.id} className={`recent__item${j.id === currentId ? ' recent__item--on' : ''}`}>
            <button type="button" className="recent__open" onClick={() => onOpen(j)}>
              <span className="recent__title">
                <HistoryOutlined /> {summaryTitle(j)}
              </span>
              <span className="recent__meta">
                {p.adults} adult{p.adults === 1 ? '' : 's'} · {String(p.cabinClass).replace('_', ' ').toLowerCase()}
                {p.type === 'ROUNDTRIP' && p.stayLengthMin ? ` · ${p.stayLengthMin}${p.stayLengthMax && p.stayLengthMax !== p.stayLengthMin ? `–${p.stayLengthMax}` : ''} nights` : ''}
                {' · '}
                {fmtDateTime(j.createdAt)}
              </span>
              <span className="recent__foot">
                {j.status === 'running' ? (
                  <Tag color="processing" bordered={false}>
                    running {pct}%
                  </Tag>
                ) : j.status === 'cancelled' ? (
                  <Tag bordered={false}>stopped at {pct}%</Tag>
                ) : j.status === 'error' ? (
                  <Tag color="red" bordered={false}>
                    failed
                  </Tag>
                ) : (
                  <Tag color="green" bordered={false}>
                    done
                  </Tag>
                )}
                {j.cheapestPrice != null && <Typography.Text strong>from {fmtPrice(j.cheapestPrice, j.currency)}</Typography.Text>}
              </span>
            </button>
            <Popconfirm title="Delete this search and its results?" onConfirm={() => onDelete(j)}>
              <Tooltip title="Delete">
                <Button size="small" type="text" icon={<DeleteOutlined />} className="recent__del" />
              </Tooltip>
            </Popconfirm>
          </div>
        );
      })}
    </div>
  );
}
